






















































































































































import {Component, Vue, Watch} from "vue-property-decorator";
import TDSButton from "@/components/common/TDSButton.vue";
import TDSModal from "@/components/partials/TDSModal/TDSModal.vue";
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import {CartItem} from "@/interfaces/entities/CartItem";
import {CartPrice} from "@/interfaces/entities/CartPrice";
import {PaymentMethod} from "@/interfaces/entities/PaymentMethod";
import {SubmitPaymentResponse} from "@/interfaces/httpResponses/SubmitPaymentResponse";
import {SubmitPaymentRequest} from "@/interfaces/httpRequests/SubmitPaymentRequest";
import TDSError from "@/components/common/TDSError.vue";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import {PurchaseHistoryEntry, PurchaseHistoryEntryItem} from "@/interfaces/entities/PurchaseHistoryEntry";

const purchaseInitialisedAtStoreKey = "purchaseInitialisedAt";
const processingTimeout = 1000 * 30;

@Component({
    components: {DefaultLayout, TDSModal, TDSButton, TDSError, TDSSpinner}
})
export default class Checkout extends Vue {
    private cartItems: CartItem[] = [];
    private cartPrice: CartPrice | null = null;
    private showOptions: boolean = false;
    private selectedPaymentMethod: PaymentMethod | null = null;
    private paymentMethods: Array<PaymentMethod> = [];
    private error: string = "";
    private showErrorModal: boolean = false;
    private isProcessingPayment: boolean = true;
    private timeoutStart: number = 0;
    private isSubmittingPayment: boolean = false;

    get errorMessage(): string {
        return this.$route.query.message as string ?? "";
    }

    get isMobile(): boolean {
        return this.$store.state.isMobile;
    }

    get submitPaymentRequestBody(): SubmitPaymentRequest {
        if (!this.selectedPaymentMethod) {
            this.error = this.$t("paymentError.paymentMethodMissing").toString();
        }
        return {
            cart: this.cartItems.map((c: CartItem) => {
                return {
                    dataPackageId: c.id,
                    amount: c.amount
                };
            }),
            paymentMethodId: this.selectedPaymentMethod?.id ?? "",
            expectedPrices: this.cartPrice?.totalPrices ?? [],
            useIncentivesBalance: false,
            token: "",

            // Those properties are ignore by server...
            // You need to set the URLs in JHipster Backoffice of TDPay... ... ...
            successCallbackUrl: "",
            errorCallbackUrl: "",
            cancelCallbackUrl: ""
        };
    }

    created() {
        this.$store.commit("SET_PREVIOUS_VIEW", "/data-packages");
        document.title = process.env.VUE_APP_THEME_TITLE + " | " + this.$t("checkout.pageTitle");
        this.readValuesFromQuery();
        this.fetchData();
    }

    @Watch("$route")
    routeChanged() {
        this.$store.commit("SET_PREVIOUS_VIEW", "/data-packages");
    }

    async fetchData() {
        this.cartItems = this.$cart.getAllItems();
        if (this.cartItems.length === 0)
            this.error = this.$t("paymentError.cartIsEmpty").toString();
        this.cartPrice = await this.$cart.fetchCartPrice();
        this.paymentMethods = await this.$store.dispatch(
            "FETCH_PAYMENT_METHODS",
            this.cartPrice?.totalPrices
        );
        this.selectedPaymentMethod = this.paymentMethods[0];
        if (this.isProcessingPayment) {
            this.timeoutStart = Date.now();
            this.processPayment();
        }
    }

    @Watch("$route.params")
    paramsChanged() {
        this.readValuesFromQuery();
    }

    //  After the redirect from the payment service like Stripe or PayPal,
    //  the user lands back on that page. We fetch the payment status from
    //  our server API. During that, the status is: isProcessPayment === true
    private async processPayment() {
        this.$cart.emptyCart();
        const purchaseInitialisedAt = Number(window.localStorage.getItem(purchaseInitialisedAtStoreKey));
        if (!purchaseInitialisedAt) {
            console.error("[Checkout] Payment wasn't initialised by this client!", purchaseInitialisedAt);
            this.$store.commit("SET_PREVIOUS_VIEW", "/data-packages");
            await this.$router.push({query: {error: "true"}});
            return;
        }
        const purchaseHistory: Array<PurchaseHistoryEntry> = await this.$store.dispatch("FETCH_PURCHASE_HISTORY");
        let isFailedPayment = false;
        const purchaseHappened = purchaseHistory.some((purchaseHistoryEntry: PurchaseHistoryEntry): boolean => {
            return purchaseHistoryEntry.items.some((purchaseHistoryEntryItem: PurchaseHistoryEntryItem): boolean => {
                if (Date.parse(purchaseHistoryEntryItem.dateTime) > purchaseInitialisedAt) {
                    isFailedPayment = purchaseHistoryEntryItem.status === "FAILURE";
                    return ["FAILURE", "SUCCESS"].includes(purchaseHistoryEntryItem.status);
                }
                return false;
            });
        });
        await this.handlePaymentResult(isFailedPayment, purchaseHappened);
    }

    private async handlePaymentResult(isFailedPayment: boolean, purchaseHappened: boolean) {
        if (!purchaseHappened && Date.now() - this.timeoutStart < processingTimeout) {
            setTimeout(() => {
                this.processPayment();
            }, 2000);
        } else if (purchaseHappened && !isFailedPayment) {
            window.localStorage.removeItem(purchaseInitialisedAtStoreKey);
            this.$store.commit("SET_PREVIOUS_VIEW", "/data-packages");
            await this.$router.push("/data-packages/checkout/success");
        } else if (purchaseHappened && isFailedPayment) {
            window.localStorage.removeItem(purchaseInitialisedAtStoreKey);
            this.$store.commit("SET_PREVIOUS_VIEW", "/data-packages");
            await this.$router.push({query: {error: "true"}});
        } else { // no purchase happened (or is pending) and timeout exceeded...
            window.localStorage.removeItem(purchaseInitialisedAtStoreKey);
            this.$store.commit("SET_PREVIOUS_VIEW", "/data-packages");
            await this.$router.push("/data-packages/checkout/success?pending=true");
        }
    }

    private readValuesFromQuery() {
        this.showErrorModal = this.$route.query.error === "true";
        this.isProcessingPayment = this.$route.query["process-payment"] === "true";
    }

    private async submit() {
        this.isSubmittingPayment = true;
        await this.performWebRedirectPayment();
        this.isSubmittingPayment = false;
    }

    private async performWebRedirectPayment() {
        try {
            const response: SubmitPaymentResponse = await this.$store.dispatch(
                "SUBMIT_PAYMENT",
                this.submitPaymentRequestBody
            );
            console.log("[checkout] Initialised Payment: ", response.data);
            window.localStorage.setItem(purchaseInitialisedAtStoreKey, (Date.now() - 5000) + "");
            window.location.assign(response.data.paymentUrl);
        } catch (e) {
            this.$store.commit("SET_PREVIOUS_VIEW", "/data-packages");
            await this.$router.push({query: {error: "true"}});
        }
    }

}
